import { FC, useEffect, useRef, useState } from "react";
import {
  MuiBox,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { ProjectFeeDetailsWrapper } from "./FeeDetails.style";

import { useSelector } from "react-redux";
import {
  GetProgramFeeResponse,
  IdNameList,
} from "../../../../../../../../api/services/common/models";
import { BillingTypeConfigureTable } from "../../../../../../../../components/billingTypeConfigureTable/BillingTypeConfigureTable";
import { BillingConfigurationDetailsFormKeyProps } from "../../../../../../../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { CurrencyFormat } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import {
  keyDetailsSelector,
  projectStatusSelector,
} from "../../../../../../../../redux/slices/project/projectSelector";
import { projectFeeDetailsCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { BillingTypeFieldInvoice } from "./../billingTypeFieldInvoice/BillingTypeFieldInvoice";

interface FeeDetailsProps {
  pageId: string;
  clientId: string;
  isEditProject?: boolean;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  defaultUserCurrency: string;
  currenciesList: IdNameList[];
  currencyName: string;
  forceUpdate: () => void;
  billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps;
  projectFeeDetailsData?: GetProgramFeeResponse;
  resetData?: boolean;
}

const FeeDetails: FC<FeeDetailsProps> = ({
  pageId,
  isEditProject,
  currencyFormat,
  currencyId,
  defaultUserCurrency,
  currenciesList,
  currencyName,
  forceUpdate,
  billingConfigurationDetailsFormKeys,
  projectFeeDetailsData,
  resetData,
}) => {
  const billingTypeFieldInvoiceRef = useRef<{
    handleConfirmationStatusTrue: () => void;
    handleConfirmationStatusFalse: () => void;
  }>(null);
  const billingTypeConfigureTableRef = useRef<{
    resetData: () => void;
  }>(null);

  const keyDetails = useSelector(keyDetailsSelector);
  const projectStatus = useSelector(projectStatusSelector);
  const [showWarningOnBillingChange, setShowWarningOnBillingChange] =
    useState(false);

  const { fetchLabel, commonLabels } = useSiteCore();

  const changeBillingConfirmationLabel = fetchLabel(
    projectFeeDetailsCoreKeys.projectFeeDetails,
    projectFeeDetailsCoreKeys.areYouSureYouWantToChangeTheBillingType,
  );

  const handleModalClose = () => {
    setShowWarningOnBillingChange(false);
    billingTypeFieldInvoiceRef.current?.handleConfirmationStatusFalse();
  };

  const handleBillingChange = () => {
    billingTypeFieldInvoiceRef.current?.handleConfirmationStatusTrue();
    setShowWarningOnBillingChange(false);
    setTimeout(() => {
      forceUpdate();
    }, 200);
  };

  useEffect(() => {
    billingTypeConfigureTableRef.current?.resetData();
  }, [resetData]);

  return (
    <ProjectFeeDetailsWrapper>
      <form>
        <MuiBox className="project-details-container">
          <BillingTypeFieldInvoice
            ref={billingTypeFieldInvoiceRef}
            currencyFormat={currencyFormat}
            currencyId={currencyId}
            defaultUserCurrency={defaultUserCurrency}
            currenciesList={currenciesList || []}
            forceUpdate={forceUpdate}
            pageId={pageId}
            setShowWarningOnBillingChange={setShowWarningOnBillingChange}
            projectStatus={projectStatus ?? ""}
            projectFeeDetailsData={projectFeeDetailsData}
          />
          <BillingTypeConfigureTable
            ref={billingTypeConfigureTableRef}
            pageId={pageId}
            currencyName={currencyName}
            currencyFormat={currencyFormat}
            currencyId={currencyId}
            forceUpdate={forceUpdate}
            programTemplateId={""}
            isInEditMode={isEditProject}
            isFromBillingInvoice={true}
            isFromProject
            billingConfigurationDetailsFormKeys={
              billingConfigurationDetailsFormKeys
            }
            clientId={keyDetails?.client?.id ?? ""}
          />
        </MuiBox>

        {showWarningOnBillingChange && (
          <WarningConfirmationModal
            show={showWarningOnBillingChange}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleBillingChange}
            secondaryButtonTitle={commonLabels.no}
            labelContent={changeBillingConfirmationLabel}
          />
        )}
      </form>
    </ProjectFeeDetailsWrapper>
  );
};

export default FeeDetails;
